import React, { Fragment, useState, useEffect, useCallback, useContext } from 'react';
import { Form, Row } from 'reactstrap';
import { X, Book, BookOpen } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import CustomContext from '../../_helper/Customizer';
import Leftbar from './Leftbar/index';
import RightHeader from './RightHeader/index';
import { MENUITEMS } from '../Sidebar/Menu';
import { Loading } from '../../Constant';
import { useAuth } from '../../_helper/Auth/AuthProvider';
import { useGetCoursesByUserEducationalLevel } from '../../queries';
import { useGetChaptersByEducationalLevel } from '../../queries';
import Fuse from 'fuse.js';
import { generateCourseUrl, generateChapterUrl } from '../../utils/urlUtils';

const Header = () => {
  const navigate = useNavigate();
  const { toggleIcon } = useContext(CustomContext);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [, setSearchResultEmpty] = useState(false);
  const { customizer } = useContext(CustomContext);
  const { userData } = useAuth();
  const userRole = userData?.role;
  const layout_type = customizer.settings.layout_type;
  const mainmenu = MENUITEMS[userRole];

  const { data: coursesData, isLoading: isCoursesLoading, isError: isCoursesError } = useGetCoursesByUserEducationalLevel(userRole === 'user');
  const { data: chaptersData, isLoading: isChaptersLoading, isError: isChaptersError } = useGetChaptersByEducationalLevel(userRole === 'user');

  const escFunction = useCallback((event) => {
    if (event.keyCode === 27) {
      setSearchInput('');
      setSearchResults([]);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const handleSearchKeyword = (keyword) => {
    setSearchInput(keyword);
    if (keyword) {
      addFix();
      const items = [];

      const fuseOptions = {
        keys: ['title'],
        threshold: 0.3,
      };

      if (userRole === 'user' && !isCoursesLoading && !isCoursesError && !isChaptersLoading && !isChaptersError) {
        const fuseCourses = new Fuse(coursesData || [], fuseOptions);
        const fuseChapters = new Fuse(chaptersData || [], fuseOptions);

        const courseResults = fuseCourses.search(keyword).map(result => ({
          title: result.item.title,
          path: generateCourseUrl(result.item.id),
          icon: <Book />,
          type: 'cours',
        }));

        const chapterResults = fuseChapters.search(keyword).map(result => ({
          title: result.item.title,
          path: generateChapterUrl(result.item.id),
          icon: <BookOpen />,
          type: 'chapiter',
        }));

        items.push(...courseResults, ...chapterResults);
      }

      mainmenu.forEach((menuItems) => {
        menuItems.Items.forEach((mItems) => {
          if (mItems.title.toLowerCase().includes(keyword.toLowerCase()) && mItems.type === 'link') {
            items.push({ ...mItems, type: 'page' });
          }
          if (mItems.children) {
            mItems.children.forEach((subItems) => {
              if (subItems.title.toLowerCase().includes(keyword.toLowerCase()) && subItems.type === 'link') {
                subItems.icon = mItems.icon;
                items.push({ ...subItems, type: 'page' });
              }
              if (subItems.children) {
                subItems.children.forEach((suSubItems) => {
                  if (suSubItems.title.toLowerCase().includes(keyword.toLowerCase())) {
                    suSubItems.icon = mItems.icon;
                    items.push({ ...suSubItems, type: 'page' });
                  }
                });
              }
            });
          }
        });
      });

      checkSearchResultEmpty(items);
      setSearchResults(items);
    } else {
      removeFix();
      setSearchResults([]);
    }
  };

  const checkSearchResultEmpty = (items) => {
    if (!items.length) {
      setSearchResultEmpty(true);
      document.querySelector('.empty-menu').classList.add('is-open');
    } else {
      setSearchResultEmpty(false);
      document.querySelector('.empty-menu').classList.remove('is-open');
    }
  };

  const addFix = () => {
    document.querySelector('.Typeahead-menu').classList.add('is-open');
    document.body.classList.add(`${layout_type}`);
  };

  const removeFix = () => {
    setSearchResults([]);
    setSearchInput('');
    document.querySelector('.Typeahead-menu').classList.remove('is-open');
    document.body.classList.add(`${layout_type}`);
    document.body.classList.remove('offcanvas');
  };

  const handleResultClick = (path) => {
    removeFix();
    navigate(path);
  };

  const getBadgeClass = (type) => {
    switch (type) {
      case 'cours':
        return 'badge-primary';
      case 'chapiter':
        return 'badge-secondary';
      case 'page':
      default:
        return 'badge-light-info';
    }
  };

  return (
    <Fragment>
      <div className={`page-header ${toggleIcon ? 'close_icon' : ''}`}>
        <Row className='header-wrapper m-0'>
          <Form className='form-inline search-full col' action='#' method='get'>
            <div className='form-group w-100'>
              <div className='Typeahead Typeahead--twitterUsers'>
                <div className='u-posRelative'>
                  <input
                    className='Typeahead-input form-control-plaintext w-100'
                    id='demo-input'
                    type='search'
                    placeholder='Rechercher Ecoline ...'
                    value={searchInput}
                    onChange={(e) => handleSearchKeyword(e.target.value)}
                  />
                  <div className='spinner-border Typeahead-spinner' role='status'>
                    <span className='sr-only'>{Loading}</span>
                  </div>
                  <X className='close-search' onClick={() => document.querySelector('.search-full').classList.remove('open')} />
                </div>
                <div className='Typeahead-menu' id='search-outer'>
                  <div className='header-search-suggestion custom-scrollbar'>
                    {searchResults.length > 0
                      ? searchResults.map((data, index) => (
                          <div
                            className='ProfileCard u-cf'
                            key={index}
                            onClick={() => handleResultClick(data.path)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className='ProfileCard-details'>
                              <div className='ProfileCard-realName'>
                                <div className='realname w-100 d-flex justify-content-start gap-2'>
                                  {data.icon}
                                  {data.title}
                                </div>
                                <span className={`badge ${getBadgeClass(data.type)}`}>
                                  {data.type.charAt(0).toUpperCase() + data.type.slice(1)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      : ''}
                  </div>
                </div>
                <div className='Typeahead-menu empty-menu'>
                  <div className='tt-dataset tt-dataset-0'>
                    <div className='EmptyMessage'>{'Oups!! Aucun résultat trouvé.'}</div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
          <Leftbar />
          <RightHeader />
        </Row>
      </div>
    </Fragment>
  );
};

export default Header;